import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useState } from 'react';
import axios from 'axios';
import {
  FileText,
  CheckCircle,
  Clock,
  Shield,
  Users,
  Phone,
  ArrowRight,
  FileCheck,
  Calculator,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Star,
  Calendar,
  Upload,
  ClipboardCheck,
  Send,
  Headphones,
  DollarSign,
  UserCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const GstFilingPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/contact`, {
        ...formData,
        service_type: 'GST Filing',
        message: 'Callback request for GST Filing consultation',
        source_page: 'GST Filing'
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const whyChooseUs = [
    {
      icon: Users,
      title: 'Expert Team',
      description: 'Skilled professionals with in-depth knowledge of GST laws and regulations.',
    },
    {
      icon: Clock,
      title: 'Timely Filing',
      description: 'Avoid penalties with our prompt and reliable filing services.',
    },
    {
      icon: DollarSign,
      title: 'Transparent Pricing',
      description: 'No hidden charges; complete clarity on costs upfront.',
    },
    {
      icon: Headphones,
      title: 'Real-Time Support',
      description: 'Get assistance whenever you need it from our dedicated team.',
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Share Your Details',
      description: 'Provide us with your business data and required documents.',
      icon: Upload,
    },
    {
      step: '02',
      title: 'Review & Calculation',
      description: 'Our experts review your input and calculate liabilities accurately.',
      icon: Calculator,
    },
    {
      step: '03',
      title: 'Return Preparation',
      description: 'We prepare your GST return with 100% accuracy.',
      icon: ClipboardCheck,
    },
    {
      step: '04',
      title: 'Filing & Confirmation',
      description: 'We file your return on time and provide confirmation.',
      icon: Send,
    },
  ];

  const documentsRequired = [
    'Sales Invoices',
    'Purchase Invoices',
    'Bank Statement',
    'Credit Notes',
    'Debit Notes',
    'Amended Invoices',
    'Accounting Data',
    'Previous Return Details',
  ];

  const gstReturnTypes = [
    { type: 'GSTR-1', dueDate: '11th of following month', description: 'Outward supplies details' },
    { type: 'GSTR-3B', dueDate: '20th of following month', description: 'Summary return with tax payment' },
    { type: 'GSTR-4', dueDate: '18th after quarter end', description: 'Composition scheme return' },
    { type: 'GSTR-5', dueDate: '20th of following month', description: 'Non-resident taxable person' },
    { type: 'GSTR-6', dueDate: '13th of following month', description: 'Input Service Distributor' },
    { type: 'GSTR-7', dueDate: '10th of following month', description: 'TDS return' },
    { type: 'GSTR-8', dueDate: '10th of following month', description: 'E-commerce operator TCS' },
    { type: 'GSTR-9', dueDate: '31st December', description: 'Annual return' },
  ];

  const whoCanFile = [
    'All registered taxpayers in India',
    'Casual taxable persons (e.g., exhibitors)',
    'Non-resident taxable persons',
    'Input Service Distributors',
    'Persons liable for TDS deduction',
    'E-commerce operators',
  ];

  const services = [
    {
      icon: FileText,
      title: 'GST Consultation',
      description: 'Expert guidance on GST compliance and filing requirements.',
    },
    {
      icon: Calendar,
      title: 'Monthly GST Filing',
      description: 'Regular filing of GSTR-1, GSTR-3B, and other monthly returns.',
    },
    {
      icon: Shield,
      title: 'GST Updates & Advisory',
      description: 'Stay informed about latest GST amendments and their impact.',
    },
    {
      icon: FileCheck,
      title: 'GST Audit Support',
      description: 'Complete assistance during GST audits and assessments.',
    },
  ];

  const faqs = [
    {
      question: 'Does GST apply to all businesses?',
      answer: 'Yes, GST applies to all service providers, manufacturers, and traders. This includes dealers, freelancers, import-export businesses, startups, LLPs, proprietorships, partnerships, and private limited companies.',
    },
    {
      question: 'Is GST to be paid monthly?',
      answer: 'Yes, most registered dealers must file and pay GST monthly. Those under the composition scheme have quarterly filing requirements. Additionally, all registered dealers must file an annual return.',
    },
    {
      question: 'What happens if GST returns are not filed?',
      answer: 'Late filing has cascading effects - subsequent returns cannot be filed until previous ones are completed. This leads to heavy penalties including late fees of ₹50/day (₹20 for nil returns) plus interest on tax due.',
    },
    {
      question: 'Can I revise a GST return after filing?',
      answer: 'GST returns cannot be revised once filed. However, corrections can be made in subsequent returns. It\'s crucial to ensure accuracy before submission, which is why professional assistance is recommended.',
    },
    {
      question: 'What is the penalty for late GST filing?',
      answer: 'Late filing attracts a penalty of ₹50 per day (₹20 for nil returns) subject to a maximum of ₹5,000. Additionally, interest at 18% per annum is charged on the outstanding tax amount.',
    },
  ];

  return (
    <div data-testid="gst-filing-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section 
        data-testid="gst-filing-hero"
        className="relative py-16 md:py-24 overflow-hidden"
      >
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left Content */}
            <div className="space-y-6">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-slate-400 text-sm">4.9 (500+ ratings)</span>
              </div>

              <h1 className="font-heading font-bold text-4xl sm:text-5xl text-white leading-tight">
                GST Return Filing{' '}
                <span className="text-gradient">Services</span>
              </h1>

              <p className="text-lg text-slate-400 leading-relaxed">
                Accurate, hassle-free, and timely GST compliance for your business needs. 
                Our expert team handles every step from calculation to filing, ensuring you stay compliant.
              </p>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>Quick & Easy Filing</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>Expert Tax Advice</span>
                </div>
                <div className="flex items-center gap-2 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-blue-400" />
                  <span>100% Accuracy</span>
                </div>
              </div>

              <a href="tel:+919717705640">
                <Button 
                  data-testid="hero-call-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
                >
                  <Phone className="mr-2 w-5 h-5" />
                  Call: +91-9717705640
                </Button>
              </a>
            </div>

            {/* Right Form */}
            <div 
              id="contact-form"
              className="bg-navy-800 border border-white/10 rounded-2xl p-8"
            >
              {isSubmitted ? (
                <div data-testid="callback-success" className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-green-400" />
                  </div>
                  <h3 className="font-heading font-bold text-xl text-white mb-2">
                    Request Received!
                  </h3>
                  <p className="text-slate-400">
                    Our GST expert will call you back shortly.
                  </p>
                </div>
              ) : (
                <>
                  <h3 className="font-heading font-bold text-xl text-white mb-6">
                    Get Free GST Filing Consultation
                  </h3>
                  <form onSubmit={handleSubmit} data-testid="callback-form" className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-slate-300">Your Name</Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="callback-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your name"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-slate-300">Contact Number</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="callback-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-slate-300">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="callback-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-navy-900 border-white/10 text-white placeholder:text-slate-500"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="callback-submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-500 hover:bg-blue-600 text-white py-6"
                    >
                      {isSubmitting ? 'Submitting...' : 'Get Free Consultation'}
                    </Button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section 
        data-testid="why-choose-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Why Choose Us</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Why Choose TaxCompany for GST Filing?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <div
                key={index}
                data-testid={`why-choose-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 text-center card-hover group"
              >
                <div className="w-14 h-14 mx-auto rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <item.icon className="w-7 h-7 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About GST Filing */}
      <section 
        data-testid="about-gst-filing"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Our Service</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                GST Filing Service
              </h2>
              <div className="space-y-4 text-slate-400 leading-relaxed">
                <p>
                  Managing GST filings can be complex and time-consuming, but our expert 
                  GST Filing Service ensures seamless compliance for businesses of all sizes.
                </p>
                <p>
                  We handle every step of the process, from accurate calculation of GST 
                  liability to timely filing of returns, ensuring you remain fully compliant 
                  with government regulations.
                </p>
                <p>
                  Our team stays updated with the latest GST laws and amendments, so you 
                  don't have to worry about navigating the complexities. With a focus on 
                  precision and efficiency, we guarantee error-free submissions.
                </p>
              </div>
              <a href="tel:+919717705640" className="inline-block mt-6">
                <Button 
                  data-testid="call-now-button"
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  <Phone className="mr-2 w-4 h-4" />
                  Call: +91-9717705640
                </Button>
              </a>
            </div>
            <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-xl text-white mb-6">
                Customized Solutions for Your Business
              </h3>
              <p className="text-slate-400 mb-6">
                Every business is unique, and our GST Filing Service is tailored to meet 
                your specific requirements. We provide personalized solutions including:
              </p>
              <ul className="space-y-3">
                {[
                  'Monthly & Quarterly Return Filing',
                  'Input Tax Credit Reconciliation',
                  'GST Audit Support',
                  'Amendment & Correction Assistance',
                  'Annual Return Preparation',
                  'Compliance Advisory Services',
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <section 
        data-testid="process-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Our Process</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400">
              Simple 4-step process to get your GST returns filed accurately and on time.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                data-testid={`process-step-${index}`}
                className="relative"
              >
                <div className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover h-full">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="text-blue-400 font-heading font-bold text-sm mb-2">
                    Step {step.step}
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-white mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-400 text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents & Who Can File */}
      <section 
        data-testid="documents-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Documents Required */}
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Documentation</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Documents Required for GST Filing
              </h2>
              <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
                <div className="grid grid-cols-2 gap-4">
                  {documentsRequired.map((doc, index) => (
                    <div 
                      key={index}
                      data-testid={`document-${index}`}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-blue-400" />
                      </div>
                      <span className="text-slate-300 text-sm">{doc}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Who Can File */}
            <div>
              <span className="text-blue-400 font-medium mb-4 block">Eligibility</span>
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
                Who Can File GST Returns?
              </h2>
              <div className="bg-navy-800 border border-white/5 rounded-2xl p-8">
                <p className="text-slate-400 mb-6">
                  All registered taxpayers in India must file GST returns. This includes:
                </p>
                <ul className="space-y-3">
                  {whoCanFile.map((item, index) => (
                    <li 
                      key={index}
                      data-testid={`who-can-file-${index}`}
                      className="flex items-start gap-3"
                    >
                      <UserCheck className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Due Dates Table */}
      <section 
        data-testid="due-dates-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Important Dates</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              GST Return Due Dates
            </h2>
            <p className="text-slate-400">
              File your returns before these dates to avoid penalties.
            </p>
          </div>

          <div className="bg-navy-800 border border-white/5 rounded-2xl overflow-hidden">
            <div className="grid grid-cols-3 gap-4 p-4 bg-navy-900/50 border-b border-white/5">
              <div className="font-heading font-semibold text-white">Return Type</div>
              <div className="font-heading font-semibold text-white">Due Date</div>
              <div className="font-heading font-semibold text-white">Description</div>
            </div>
            {gstReturnTypes.map((item, index) => (
              <div 
                key={index}
                data-testid={`due-date-${index}`}
                className="grid grid-cols-3 gap-4 p-4 border-b border-white/5 last:border-0 hover:bg-navy-900/30 transition-colors"
              >
                <div className="text-blue-400 font-medium">{item.type}</div>
                <div className="text-slate-300">{item.dueDate}</div>
                <div className="text-slate-400 text-sm">{item.description}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <a href="#contact-form">
              <Button 
                data-testid="file-now-button"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                File GST Return Now
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Penalty Section */}
      <section 
        data-testid="penalty-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-2xl p-8 md:p-12">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-7 h-7 text-red-400" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-2xl text-white mb-4">
                  Penalties for Late GST Filing
                </h2>
                <div className="space-y-3 text-slate-300">
                  <p>
                    Not filing GST returns on time can result in severe consequences:
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Late fee of ₹50/day (₹20 for nil returns) up to ₹5,000</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Interest at 18% per annum on outstanding tax</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Subsequent returns cannot be filed until previous ones are cleared</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-red-400">•</span>
                      <span>Loss of Input Tax Credit benefits</span>
                    </li>
                  </ul>
                </div>
                <a href="#contact-form" className="inline-block mt-6">
                  <Button 
                    data-testid="avoid-penalty-button"
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                  >
                    File Your Returns Now
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services We Offer */}
      <section 
        data-testid="services-section"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-blue-400 font-medium mb-4 block">Our Services</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              What We Offer in GST Filing
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                data-testid={`service-card-${index}`}
                className="bg-navy-800 border border-white/5 rounded-2xl p-6 card-hover group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  <service.icon className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-400 text-sm">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section 
        data-testid="faq-section"
        className="py-16 md:py-24"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-blue-400 font-medium mb-4 block">FAQs</span>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                data-testid={`faq-${index}`}
                className="bg-navy-800 border border-white/5 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left"
                >
                  <span className="font-heading font-semibold text-white pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-blue-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5">
                    <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-slate-400 mb-4">Still have questions?</p>
            <a href="tel:+919717705640">
              <Button 
                data-testid="faq-call-button"
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <Phone className="mr-2 w-4 h-4" />
                Call: +91-9717705640
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section 
        data-testid="final-cta"
        className="py-16 md:py-24 bg-navy-800/50"
      >
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-gradient-to-r from-blue-500/10 to-sky-500/10 border border-white/10 rounded-3xl p-10 md:p-14 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-4">
                Ready to File Your GST Returns?
              </h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">
                Our expert team ensures accurate and timely GST filing. 
                Get started today and stay compliant!
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="#contact-form">
                  <Button 
                    data-testid="final-cta-form"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg"
                  >
                    Request Callback
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </a>
                <Link to="/contact">
                  <Button 
                    data-testid="final-cta-contact"
                    variant="outline"
                    className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                  >
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
